// External Imports
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import { isoDateToString } from "../../util/format-date";
import ChargingCard from "./ChargingCard";
import Heading from "../UI/Heading";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import DetailPair from "../UI/DetailPair";
import InvoiceModal from "../payments-page/InvoiceModal";

/** ========================================================================
 * * Booking Charging Page
 * Renders a page, which displays EV charging consumed during a booking.
 * ========================================================================== */

export default function BookingCharging() {
  // Load Context
  const { navigate } = useContext(UtilityContext);

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState({});
  const [chargings, setChargings] = useState([]);
  const [selected, setSelected] = useState([]);
  const [total, setTotal] = useState(0);
  const [invoice, setInvoice] = useState(false);

  // Get Booking and Customer from URL
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("bookingId");

  /* ======================= FETCH BOOKING CHARGING ======================= */
  const fetchCharging = async () => {
    try {
      setLoading(true);
      if (!bookingId) return;
      // Fetch Booking
      const booking = await requestAdminAPI("fetch", {
        table: "booking",
        id: bookingId,
        columns: [
          "vehicle { id model year vin }",
          "customer { id firstName lastName }",
        ],
      });

      // Fetch Charging
      const response = await requestAdminAPI("filter", {
        table: "charging",
        filters: [
          `id: { ne: "latest" }`,
          `vin: { contains: "${booking.vehicle?.vin}" }`,
          `date: {ge: "${booking.startDate}"}`,
          `date: {le: "${booking.endDate}"}`,
        ],
        columns: ["vehicle { vin make { name } model year }"],
      });
      // Sort Charging by date
      response.sort((a, b) => new Date(b.date) - new Date(a.date));

      setBooking(booking);
      setChargings(response);
      selectPendingHandler(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= TOTAL SELECTED RECORDS ======================= */
  const calculateTotal = () => {
    const selectedCharging = selected.filter((charge) => charge.selected);
    let total = 0;
    selectedCharging.forEach((charge) => {
      total += Number(charge.total);
    });
    return Number((total * 1.039).toFixed(2));
  };

  /* ======================= SELECT CHARGING RECORD ======================= */
  const selectHandler = (charge) => {
    charge.selected = !charge.selected;
    setSelected([...selected]);
  };

  /* ======================= SELECT ALL CHARGING RECORDS ======================= */
  const selectAllHandler = (charging) => {
    charging.forEach((charge) => {
      charge.selected = true;
    });
    setSelected([...charging]);
  };

  /* ======================= SELECT ALL CHARGING RECORDS ======================= */
  const selectPendingHandler = (chargings) => {
    chargings.forEach((charge) => {
      if (!charge.invoiced) charge.selected = true;
    });
    setSelected([...chargings]);
  };

  /* ======================= UNSELECT ALL CHARGING RECORDS ======================= */
  const unselectAllHandler = () => {
    selected.forEach((charge) => {
      charge.selected = false;
    });
    setSelected([...selected]);
  };

  /* ======================= MARK AS INVOICE ======================= */
  const markAsInvoiceHandler = async (paymentId) => {
    try {
      setLoading(true);
      const selectedCharging = selected.filter((charge) => charge.selected);
      await requestAdminAPI("mark-chargings", {
        chargings: selectedCharging.map((charge) => charge.id),
      });
      navigate("/payments/info?id=" + paymentId);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Fetch Booking Charging on load
  useEffect(() => {
    fetchCharging();
  }, []);

  // Update Total
  useEffect(() => {
    setTotal(calculateTotal());
  }, [selected]);

  if (!bookingId)
    return (
      <div className="container">
        <p className="text-white">Booking ID not provided!</p>
      </div>
    );

  return (
    <div className="container">
      <InvoiceModal
        open={invoice}
        setOpen={setInvoice}
        customerId={booking.customerId}
        bookingId={bookingId}
        amount={total}
        description={`EV Charging for ${booking.vehicle?.year} ${
          booking.vehicle?.model
        } ${booking.vehicle?.vin.substring(13, 17)} ( ${isoDateToString(
          selected[0]?.date
        )} - ${isoDateToString(selected[selected.length - 1]?.date)} )`}
        onComplete={markAsInvoiceHandler}
        category="charging"
      />
      <div className="grid gap-8">
        <Heading
          title="Booking EV Charging"
          isBackArrow={true}
          text="Total amount calculated includes a 3.9% surcharge."
        >
          <p
            onClick={() => selectPendingHandler(selected)}
            className="text-sm cursor-pointer hover:text-stone-400"
          >
            Select Pending
          </p>
          <p
            onClick={() => selectAllHandler(selected)}
            className="text-sm cursor-pointer hover:text-stone-400"
          >
            Select All
          </p>
          <p
            onClick={unselectAllHandler}
            className="text-sm cursor-pointer hover:text-stone-400"
          >
            Unselect All
          </p>
        </Heading>

        {loading ? (
          <LoadingCard columns={5} max={6} />
        ) : chargings.length > 0 ? (
          <div className="list">
            {chargings.map((charge, index) => (
              <ChargingCard
                key={index}
                charging={charge}
                selected={charge.selected}
                onClick={() => selectHandler(charge)}
              />
            ))}
          </div>
        ) : (
          <NoRecordsFound />
        )}

        <div className="items-end gap-8 info">
          <DetailPair
            label="Total"
            classes="font-semibold text-xl"
            value={`$${total}`}
            loading={loading}
          />
          <DetailPair
            label="Customer"
            value={`${booking.customer?.firstName} ${booking.customer?.lastName}`}
            loading={loading}
          />
          <DetailPair
            label="Vehicle"
            value={`${booking.vehicle?.year} ${
              booking.vehicle?.model
            } ${booking.vehicle?.vin?.substring(13, 17)}`}
            loading={loading}
          />
          <DetailPair
            label="Booking Dates"
            value={`${isoDateToString(booking.startDate)} - ${isoDateToString(
              booking.endDate
            )}`}
            loading={loading}
          />
          {total > 0 && (
            <button onClick={() => setInvoice(true)} className="btn-primary">
              Send Invoice
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
